import type { CalendarCall } from '@/store/slices/calendarSlice';

/** How many days back the Instant Call menu looks, and the window it asks the week API for. */
export const RECENT_CALLS_LOOKBACK_DAYS = 7;
export const RECENT_CALLS_WINDOW_DAYS = 9;
export const RECENT_CALLS_LIMIT = 10;

function toLocalDateStr(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Local YYYY-MM-DD of `now` minus 7 days (never toISOString: that is UTC and can shift the day). */
export function recentCallsWindowStart(now: Date): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - RECENT_CALLS_LOOKBACK_DAYS);
  return toLocalDateStr(d);
}

export function isCompletedCall(call: Pick<CalendarCall, 'end_time' | 'status'>): boolean {
  return Boolean(call.end_time) || call.status === 'completed';
}

/** Completed calls open their incident report; everything else is rejoined. */
export function recentCallDestination(call: Pick<CalendarCall, 'call_id' | 'end_time' | 'status'>): string {
  return isCompletedCall(call)
    ? `/incident-report/${encodeURIComponent(call.call_id)}`
    : `/calls/${call.call_id}`;
}

function callTime(c: CalendarCall): string {
  return c.start_time || c.scheduled_time || '';
}

/**
 * Server rows (already scoped by the API, never filtered here) plus the Redux rows that pass the
 * old owner/assignee/source filter, deduped by id with the server row winning, newest first.
 */
export function mergeRecentCalls(server: CalendarCall[], redux: CalendarCall[], userId: string | undefined): CalendarCall[] {
  if (!userId) return [];
  const byId = new Map<string, CalendarCall>();
  for (const c of redux) {
    if (c.owner_id === userId || c.assigned_officer_id === userId || c.source === 'calls') byId.set(c.id, c);
  }
  for (const c of server) byId.set(c.id, c);
  return [...byId.values()]
    .sort((a, b) => callTime(b).localeCompare(callTime(a)))
    .slice(0, RECENT_CALLS_LIMIT);
}

/** GET the recent-call inventory. Throws on any non-200 or a body without a `calls` array. */
export async function fetchRecentCalls(now: Date = new Date()): Promise<CalendarCall[]> {
  const params = new URLSearchParams({ start: recentCallsWindowStart(now), days: String(RECENT_CALLS_WINDOW_DAYS) });
  const res = await fetch(`/api/calendar/week?${params.toString()}`);
  if (!res.ok) throw new Error(`recent calls ${res.status}`);
  const body = await res.json();
  if (!body || !Array.isArray(body.calls)) throw new Error('recent calls: malformed response');
  return (body.calls as CalendarCall[]).map((c) => ({ ...c, source: 'calls' as const }));
}
